import React, { useEffect, useState } from 'react';
import { MdPeople, MdWaterDrop } from 'react-icons/md';
import { FaSeedling } from 'react-icons/fa';
import { BsTrash } from 'react-icons/bs';
import { RiFootprintLine } from 'react-icons/ri';
import { getFarmerAnswers } from '../api';
import { AiFillProduct } from "react-icons/ai";
import { getUser } from '../utils';
import InnerLoader from './microComponents/InnerLoader';
import { useApp } from '../context/AppContext';

const FarmerCard = ({ farmer = {} }) => {
    const { updateUser } = useApp()
    const [answers, setAnswers] = useState([])
    const farmerDetails = getUser()?.farmer?.farm?.farm_name
    const [loader, setLoader] = useState(false)

    useEffect(() => {
        setLoader(true);
        getFarmerAnswers().then(res => {
            // Save answers in the app context
            updateUser({ ...getUser(), answers: res?.answers })

            // Farmer Types
            const findAnswerOne = res?.answers.find(answer => answer?.question?.order === 2)
            // console.log("🚀 ~ getFarmerAnswers ~ findAnswerOne:", findAnswerOne, res?.answers)
            const answerOne = findAnswerOne?.text.map(text => text.value).join(", ")

            // Organic Regenerative Soil No-Till, Cover Crop
            const findAnswerTen = res?.answers.find(answer => answer?.question?.order === 11)
            const answerTwo = findAnswerTen?.number

            // Climate Smart Water preservation techniques
            const findAnswerEleven = res?.answers.find(answer => answer?.question?.order === 4)
            const answerThree = findAnswerEleven?.number

            // Potential GHG Emission reduced
            const findAnswerTwelve = res?.answers.find(answer => answer?.question?.order === 13)
            const answerFour = findAnswerTwelve?.number

            // Total Food Waste Reduced
            const findAnswerThirteen = res?.answers.find(answer => answer?.question?.order === 14)
            const answerFive = findAnswerThirteen?.number

            // Total Volume Product Sourced
            const findAnswerFourteen = res?.answers.find(answer => answer?.question?.order === 15)
            const answerSix = findAnswerFourteen?.number

            // Add answers to state
            setAnswers([answerOne, answerTwo, answerThree, answerFour, answerFive, answerSix])
        }).catch((error) => {
            console.log('Error:', error)
        }).finally(() => {
            setLoader(false);
        })
    }, [])

    return (
        <div className="bg-white rounded-2xl shadow-lg p-6 space-y-6">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <h2 className="text-2xl font-bold text-gray-900 flex flex-row gap-2 items-center">{farmerDetails ?? ''}
                    {loader &&
                        <InnerLoader
                            divSize={{ width: "22px", height: "22px" }}
                            loaderSize={{ width: "22px", height: "22px" }}
                        />}
                </h2>
                <div className="flex items-center gap-2">
                    <div className="bg-green-500 rounded-full w-3 h-3"></div>
                    <span className="text-xl font-semibold">{0}/100</span>
                    <span className="text-gray-500">Excellent</span>
                </div>
            </div>

            {/* Environmental and Social Impact Score */}
            <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="text-lg text-gray-600">Environmental and Social Impact Score</h3>
                <div className="flex items-center gap-2 mt-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                            className="bg-green-500 rounded-full h-2"
                            style={{ width: `${farmer.impactScore}%` }}
                        ></div>
                    </div>
                    <span className="font-semibold">{farmer.impactScore}/100</span>
                </div>
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Farmer Types */}
                <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-4">
                    <MdPeople className="text-2xl text-gray-400" />
                    <div>
                        {/* <p className="text-gray-600">{
                            answers.length > 0 && answers[0]
                        }</p> */}
                        <p className="font-medium">{
                            answers.length > 0 && answers[0]
                        }</p>
                    </div>
                    <div className="ml-auto">
                        <div className="bg-green-100 rounded-full w-6 h-6 flex items-center justify-center">
                            <span className="text-green-600">✓</span>
                        </div>
                    </div>
                </div>

                {/* Organic Regenerative Soil No-Till, Cover Crop */}
                <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-4">
                    <FaSeedling className="text-2xl text-gray-400" />
                    <div>
                        {/* <p className="text-gray-600">Farming Practices</p> */}
                        <p className="font-medium">Organic Regenerative Soil
                            No-Till, Cover Crop</p>
                    </div>
                    <div className="ml-auto">
                        <span className="text-gray-600">{answers.length > 0 && answers[1]}%</span>
                    </div>
                </div>

                {/* Climate Smart Water preservation techniques */}
                <div className="flex items-center gap-2 bg-gray-50 rounded-xl p-4">
                    <MdWaterDrop className="text-2xl text-gray-400" />
                    <div>
                        {/* <p className="text-gray-600">Water Conservation</p> */}
                        <p className="font-medium">Climate Smart
                            Water preservation techniques </p>
                    </div>
                    <div className="ml-auto min-w-24 text-right">
                        <span className="text-gray-600">{answers.length > 0 && answers[2]} Gal</span>
                    </div>
                </div>

                {/* Potential GHG Emission reduced */}
                <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-4">
                    <RiFootprintLine className="text-2xl text-gray-400" />
                    <div>
                        {/* <p className="text-gray-600">GHG Emissions</p> */}
                        <p className="font-medium">Potential GHG Emission reduced</p>
                    </div>
                    <div className="ml-auto min-w-24 text-right">
                        <span className="text-gray-600">{answers.length > 0 && answers[3]} Mg</span>
                    </div>
                </div>

                {/* Total Food Waste Reduced */}
                <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-4">
                    <BsTrash className="text-2xl text-gray-400" />
                    <div>
                        {/* <p className="text-gray-600">Food Waste</p> */}
                        <p className="font-medium">Total Food Waste Reduced</p>
                    </div>
                    <div className="ml-auto min-w-24 text-right">
                        <span className="text-gray-600">{answers.length > 0 && answers[4]} lbs</span>
                    </div>
                </div>

                {/* Total Production */}
                <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-4">
                    <AiFillProduct className="text-2xl text-gray-400" />
                    <div>
                        {/* <p className="text-gray-600">Total Production</p> */}
                        <p className="font-medium">Total Volume Product Sourced</p>
                    </div>
                    <div className="ml-auto min-w-24 text-right">
                        <span className="text-gray-600">{answers.length > 0 && answers[5]} lbs</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FarmerCard;